/**
 * PR-34B — Lightweight, privacy-safe device fingerprint.
 *
 * Combines stable, low-entropy signals (platform, timezone, language,
 * screen dimensions, UA hash) into a single SHA-256 hex string. The
 * payload is reduced to a 64-char hex digest — we never store the raw
 * UA, IP, geolocation or any other PII. Cached in localStorage so the
 * hash is stable across the session/device. Safe to call from any
 * environment (returns null on SSR).
 */
const STORAGE_KEY = "vs:device_hash:v1";

async function sha256Hex(input: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    // Last-resort hash (FNV-1a) for environments without SubtleCrypto.
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i);
      h = (h * 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(64, "0");
  }
}

function collectSignals(): string {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "";
  const tz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "?"; } catch { return "?"; }
  })();
  const screenSig = (() => {
    try {
      const w = window.screen?.width ?? 0;
      const h = window.screen?.height ?? 0;
      const dpr = window.devicePixelRatio ?? 1;
      return `${w}x${h}@${dpr}`;
    } catch { return "0x0@1"; }
  })();
  const lang = (navigator.language || "?").slice(0, 16);
  const platform = ((navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
    ?? navigator.platform ?? "?").slice(0, 32);
  const ua = (navigator.userAgent ?? "").slice(0, 512);
  return [platform, tz, lang, screenSig, ua].join("|");
}

/**
 * Returns a stable 64-char hex device hash, or null when running on the
 * server / in environments where signals can't be collected.
 */
export async function getDeviceHash(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  try {
    const cached = window.localStorage.getItem(STORAGE_KEY);
    if (cached && cached.length >= 32) return cached;
  } catch { /* noop */ }
  const raw = collectSignals();
  if (!raw) return null;
  const hash = await sha256Hex(raw);
  try { window.localStorage.setItem(STORAGE_KEY, hash); } catch { /* noop */ }
  return hash;
}

/** Hash an arbitrary string with the same algorithm (e.g. UA-only hash). */
export async function hashString(value: string): Promise<string> {
  return sha256Hex(value || "");
}